import { Search, SlidersHorizontal, ArrowUpDown, X } from "lucide-react";
import { CREW_ROLES } from "@produce/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { type TeamFilter, type TeamSortKey } from "@/lib/teamMembers";

const SORT_LABELS: Record<TeamSortKey, string> = {
  name: "Name (A–Z)",
  role: "Role",
  recent: "Recently added",
};

interface TeamToolbarProps {
  filter: TeamFilter;
  onChange: (filter: TeamFilter) => void;
  /** Gear types actually present on the team (drives the gear filter). */
  gearOptions: string[];
  /** Number of members matching the current filter, for the count chip. */
  resultCount: number;
  totalCount: number;
}

/**
 * Search + role/gear filter + sort controls for the My Team page.
 * Purely drives the `filter` value; the page applies it via
 * filterAndSortMembers().
 */
export const TeamToolbar = ({
  filter,
  onChange,
  gearOptions,
  resultCount,
  totalCount,
}: TeamToolbarProps) => {
  const toggleRole = (role: string) => {
    const roles = filter.roles.includes(role)
      ? filter.roles.filter((r) => r !== role)
      : [...filter.roles, role];
    onChange({ ...filter, roles });
  };

  const toggleGear = (gear: string) => {
    const gearTypes = filter.gearTypes.includes(gear)
      ? filter.gearTypes.filter((g) => g !== gear)
      : [...filter.gearTypes, gear];
    onChange({ ...filter, gearTypes });
  };

  const activeFilters = filter.roles.length + filter.gearTypes.length;
  const clearFilters = () => onChange({ ...filter, roles: [], gearTypes: [] });

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="relative min-w-[200px] flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
          placeholder="Search name, email, role, gear..."
          className="pl-9"
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            Filter
            {activeFilters > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {activeFilters}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-[60vh] w-56 overflow-y-auto">
          <DropdownMenuLabel>Role</DropdownMenuLabel>
          {CREW_ROLES.map((role) => (
            <DropdownMenuCheckboxItem
              key={role}
              checked={filter.roles.includes(role)}
              onCheckedChange={() => toggleRole(role)}
              onSelect={(e) => e.preventDefault()}
              className="capitalize"
            >
              {role}
            </DropdownMenuCheckboxItem>
          ))}
          {gearOptions.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Gear</DropdownMenuLabel>
              {gearOptions.map((gear) => (
                <DropdownMenuCheckboxItem
                  key={gear}
                  checked={filter.gearTypes.includes(gear)}
                  onCheckedChange={() => toggleGear(gear)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {gear}
                </DropdownMenuCheckboxItem>
              ))}
            </>
          )}
          {activeFilters > 0 && (
            <>
              <DropdownMenuSeparator />
              <button
                type="button"
                onClick={clearFilters}
                className="flex w-full items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear filters
              </button>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2">
            <ArrowUpDown className="h-4 w-4" />
            {SORT_LABELS[filter.sort]}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Organize by</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={filter.sort}
            onValueChange={(v) => onChange({ ...filter, sort: v as TeamSortKey })}
          >
            {(Object.keys(SORT_LABELS) as TeamSortKey[]).map((key) => (
              <DropdownMenuRadioItem key={key} value={key}>
                {SORT_LABELS[key]}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <span className="ml-auto whitespace-nowrap text-sm text-muted-foreground">
        {resultCount} of {totalCount}
      </span>
    </div>
  );
};

export default TeamToolbar;
